import React, { useState } from 'react'
import MenuItem from '@mui/material/MenuItem'
import Backdrop from '@mui/material/Backdrop'
import Tooltip from '@mui/material/Tooltip'
import NativeSelect, { SelectProps } from '@mui/material/Select'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { css, StyleSheet } from 'aphrodite'
import useResponsive from 'hooks/useResponsive'
import constants from 'utils/constants'

export interface IOption {
  value: number,
  label: string,
  parent?: number,
  tooltip?: string,
  disabled?: boolean,
}

interface ISelectProps extends SelectProps {
  options: IOption[],
  depth?: number,
  big?: boolean,
}

const Select = ({
  big,
  depth,
  options = [],
  defaultValue = null,
  onChange,
}: ISelectProps) => {
  const { compareTag } = useResponsive()
  const [displayBackdrop, setDisplayBackdrop] = useState(false)
  const [selectedValue] = useState(defaultValue)

  const handleSelectOpen = () => {
    setDisplayBackdrop(true)
  }
  const handleBackdropClick = () => {
    setDisplayBackdrop(false)
  }

  const bigStyles = compareTag('md') ? styles.lgBig : styles.big
  const smallStyles = compareTag('md') ? styles.lgSmall : styles.small
  const itemsStyles = compareTag('md') ? styles.selectItemBig : styles.selectItemSmall

  const renderSelectedValue = (value) => {
    const selectedVal = options.filter((option: IOption) => option.value === value).shift()
    return (
      <div>
        {selectedVal.label}
      </div>
    )
  }

  return (
    <>
      <NativeSelect
        defaultValue={selectedValue}
        IconComponent={KeyboardArrowDownIcon}
        classes={{
          select: css(styles.select, big ? bigStyles : smallStyles),
          icon: css(styles.icon),
        }}
        onOpen={handleSelectOpen}
        onClose={handleBackdropClick}
        sx={{
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
        }}
        MenuProps={{
          classes: {
            list: css(styles.list),
            paper: css(styles.paper),
          },
        }}
        className="tw-antialiased"
        onChange={onChange}
        renderValue={renderSelectedValue}
      >
        {!!options.length && options.map((option: IOption) => (
          <MenuItem
            key={option.value}
            value={option.value}
            disabled={option.disabled}
            className="tw-antialiased"
            classes={{
              root: css(styles.selectItem, itemsStyles, (depth > 1 && !option.parent ? styles.italic : null)),
            }}
          >
            {(option.tooltip && compareTag('md')) ? (
              <Tooltip
                title={(
                  <div className={css(styles.tooltipContainer)}>
                    <FontAwesomeIcon icon={faCircleInfo} color={constants.colors.white} size="3x" />
                    <div className={css(styles.tooltipContent)}>{option.tooltip}</div>
                  </div>
                )}
                placement={compareTag('md') ? 'right' : 'bottom'}
                classes={{
                  tooltip: css(styles.tooltip),
                }}
              >
                <div className={css(styles.selectItemLabel)}>{option.label}</div>
              </Tooltip>
            ) : (<div className={css(styles.selectItemLabel)}>{option.label}</div>)}
          </MenuItem>
        ))}
      </NativeSelect>
      <Backdrop open={displayBackdrop} onClick={handleBackdropClick} />
    </>
  )
}
Select.defaultProps = {
  big: false,
  depth: 1,
}

const styles = StyleSheet.create({
  big: {
    fontSize: 20,
    lineHeight: '46px',
  },
  lgBig: {
    fontSize: 46,
    lineHeight: '64px',
  },
  icon: {
    color: constants.colors.green,
    fontSize: '36px',
  },
  italic: {
    fontStyle: 'italic',
  },
  list: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  paper: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: 0,
    maxHeight: '80%',
    minWidth: 370,
    maxWidth: '85%',
  },
  root: {
    color: 'inherit',
  },
  select: {
    color: 'white',
    lineHeight: '36px',
    fontFamily: 'Public Sans, sans-serif',
    fontWeight: 800,
    textDecoration: 'underline',
    textDecorationColor: constants.colors.green,
    textDecorationThickness: '3px',
    textUnderlineOffset: '4px',
    padding: 0,
    paddingLeft: 0,
    paddingRight: '48px',
    whiteSpace: 'normal',
  },
  selectItem: {
    color: constants.colors.background,
    fontFamily: 'Public Sans, sans-serif',
    fontWeight: 700,
    whiteSpace: 'normal',
    ':hover': {
      background: constants.colors.lightGreen,
    },
  },
  selectItemBig: {
    fontSize: 30,
  },
  selectItemSmall: {
    fontSize: 20,
  },
  selectItemLabel: {
    width: '100%',
  },
  small: {
    fontSize: 20,
    lineHeight: '24px',
  },
  lgSmall: {
    fontSize: 30,
    lineHeight: '36px',
  },
  tooltip: {
    backgroundColor: 'rgba(0,0,0,.7)',
    maxWidth: '240px',
    marginLeft: '24px',
  },
  tooltipContainer: {
    display: 'flex',
    alignItems: 'center',
    padding: 16,
  },
  tooltipContent: {
    marginLeft: 16,
    fontSize: 18,
  },
})
export default Select
